/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from 'react';

// react-router-dom components
import { useLocation, NavLink } from 'react-router-dom';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @mui material components
import Divider from '@mui/material/Divider';

import intl from 'react-intl-universal';

import Icon from '@mui/material/Icon';
// Custom styles for the Sidenav
import SidenavRoot from './SidenavRoot';
import { ROUTES } from 'shared/constants/routes';

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentSidenav,
  setWhiteSidenav,
} from 'context';
import { UserProfile } from 'components/UserProfile/UserProfile';
import { createUsername } from 'utils/SidenavUtils';
import { USER_DATA } from 'shared/constants/users';
import localData from 'utils/localData';
import { SideNavActionButton } from 'components/SidenavActionButton/SidenavActionButton';
import SidenavList from 'components/SidenavList/SidenavList';
import SidenavListItem from 'components/SidenavListItem/SidenavListItem';
import SidenavListCollapse from 'components/SidenavListCollapse/SidenavListCollapse';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Box, Typography } from '@mui/material';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';
import logger from 'utils/logger';
import { useSnackbar } from 'notistack';

const PATH_OTHERS = [
  '/dashboard/personal-settings',
  '/dashboard/settings',
  '/dashboard/logs',
  '/dashboard/manual',
  '/dashboard/service-terms',
];

const log = logger.createLogger('Sidenav');

function Sidenav({
  openDrawer,
  color = 'info',
  routes,
  userInfo,
  handleClickLogout,
  ...rest
}) {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  // Should make this better
  const checkIfPathOthers = (path) => {
    return PATH_OTHERS.includes(path);
  };

  const [openMenuCollapse, setOpenMenuCollapse] = useState(
    checkIfPathOthers(location.pathname)
  );
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;

  const currentOrganization = localData.get(USER_DATA.CURRENT_ORGANIZATION);

  const changeCurrentOrganization = (newOrganization, newOrganizationId) => {
    // Checks for malicious but it doesn't stop the user
    api
      .post(`${DOLPHIN_API.USERS}/signin-organization`, {
        data: {
          username: userInfo.username,
          userId: userInfo.id,
          organizationId: newOrganizationId,
        },
      })
      .then((data) => {
        const { privilege, userType, isOwner } = data;
        localData.set(USER_DATA.CURRENT_PRIVILEGE, privilege);
        if (userType) {
          localData.set(USER_DATA.USER_TYPE, userType);
        }
        localData.set(USER_DATA.IS_OWNER, isOwner);
        localData.set(USER_DATA.CURRENT_ORGANIZATION, newOrganization);
        localData.set(USER_DATA.CURRENT_ORGANIZATION_ID, newOrganizationId);
        window.location.reload();
      })
      .catch((error) => {
        log.error('error', error);
        enqueueSnackbar(intl.get('dashboard_text_error_change_organization'), {
          variant: 'error',
        });
      });
  };

  const findRouteAndChildren = (routes, parentKey, childKeys = []) => {
    const parentRoute = routes.find((route) => route.key === parentKey);

    if (!parentRoute || !childKeys.length) {
      return { parent: parentRoute, children: {} };
    }

    const children = {};
    childKeys.forEach((childKey) => {
      children[childKey] = parentRoute.children?.find(
        (item) => item.key === childKey
      );
    });

    return { parent: parentRoute, children };
  };

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      // setMiniSidenav(dispatch, window.innerWidth < 1200); temp: DO NOT allow sidebar to minimize automatically;
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      setWhiteSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : whiteSidenav
      );
    }
    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener('resize', handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav]);

  // get all routes that should be displayed on the sidenav
  //! Unique routes
  const newSeimitsuRoute = routes.find((route) => route.key === ROUTES.NEW);
  const newKenshinRoute = routes.find(
    (route) => route.key === ROUTES.NEW_KENSHIN
  );
  const taskManagementSeimitsuRoute = routes.find(
    (route) => route.key === ROUTES.TASK_MANAGEMENT
  );
  const taskManagementKenshinRoute = routes.find(
    (route) => route.key === ROUTES.TASK_MANAGEMENT_KENSHIN
  );
  const orgSettings = routes.find((item) => item.key === ROUTES.SETTINGS);
  const logs = routes.find((item) => item.key === ROUTES.LOGS);
  const manual = routes.find((item) => item.key === ROUTES.MANUAL);
  const serviceTerms = routes.find((item) => item.key === ROUTES.SERVICE_TERMS);

  // History routes
  const { parent: histories, children: historyChildren } = findRouteAndChildren(
    routes,
    ROUTES.HISTORY,
    [ROUTES.RECEIVED, ROUTES.SENT]
  );
  const historyReceivedSeimitsuRoute = historyChildren[ROUTES.RECEIVED];
  const historySentSeimitsuRoute = historyChildren[ROUTES.SENT];

  // Kenshin History routes
  const { parent: historiesKenshin, children: historyKenshinChildren } =
    findRouteAndChildren(routes, ROUTES.HISTORY_KENSHIN, [
      ROUTES.RECEIVED_KENSHIN,
      ROUTES.SENT_KENSHIN,
    ]);
  const historyReceivedKenshinRoute =
    historyKenshinChildren[ROUTES.RECEIVED_KENSHIN];
  const historySentKenshinRoute = historyKenshinChildren[ROUTES.SENT_KENSHIN];

  // Billing routes
  const { parent: billing, children: billingChildren } = findRouteAndChildren(
    routes,
    ROUTES.BILLING,
    [ROUTES.RECEIVED_BILLING, ROUTES.SENT_BILLING]
  );
  const billingReceivedRoute = billingChildren[ROUTES.RECEIVED_BILLING];
  const billingSentRoute = billingChildren[ROUTES.SENT_BILLING];

  // Assignment routes
  const { parent: assignment, children: assignmentChildren } =
    findRouteAndChildren(routes, ROUTES.ASSIGNMENT, [
      ROUTES.RECEIVED,
      ROUTES.SENT,
      ROUTES.RECEIVED_KENSHIN,
    ]);
  const assignment_received = assignmentChildren[ROUTES.RECEIVED];
  const assignment_sent = assignmentChildren[ROUTES.SENT];
  const assignment_received_kenshin =
    assignmentChildren[ROUTES.RECEIVED_KENSHIN];

  // Contacts routes
  const { parent: contacts, children: contactsChildren } = findRouteAndChildren(
    routes,
    ROUTES.CONTACTS,
    [ROUTES.FRIENDS]
  );
  const contacts_friends = contactsChildren[ROUTES.FRIENDS];

  // Wage routes
  const { parent: paidWage, children: paidWageChildren } = findRouteAndChildren(
    routes,
    ROUTES.PAID_WAGE,
    [ROUTES.PAID_WAGE]
  );
  const paidWageDetails = paidWageChildren[ROUTES.PAID_WAGE];

  const { parent: myWage, children: myWageChildren } = findRouteAndChildren(
    routes,
    ROUTES.MY_WAGE,
    [ROUTES.MY_WAGE]
  );
  const myWageDetails = myWageChildren[ROUTES.MY_WAGE];

  return (
    <Box component="nav" data-testid="sidenav-box">
      <SidenavRoot
        {...rest}
        open={openDrawer}
        variant="persistent"
        ownerState={{
          transparentSidenav,
          whiteSidenav,
          miniSidenav,
          darkMode,
        }}>
        <UserProfile
          userName={createUsername(
            userInfo.firstName,
            userInfo.lastName,
            userInfo.preferredLanguage
          )}
          userFirstName={userInfo.firstName}
          organizationList={userInfo.organizations}
          currentOrganization={currentOrganization}
          handleChangeCurrentOrganization={changeCurrentOrganization}
          handleClickLogout={handleClickLogout}
        />

        {newSeimitsuRoute && (
          <SideNavActionButton
            dataTestId="sidenav-button-new-seimitsu"
            startIcon={newSeimitsuRoute.icon}
            color="primary"
            text={newSeimitsuRoute.name}
            route={newSeimitsuRoute.route}
          />
        )}

        {newKenshinRoute && (
          <SideNavActionButton
            dataTestId="sidenav-button-new-kenshin"
            startIcon={newKenshinRoute.icon}
            color="primary"
            text={newKenshinRoute.name}
            route={newKenshinRoute.route}
          />
        )}

        {taskManagementSeimitsuRoute && (
          <SideNavActionButton
            dataTestId="sidenav-button-kanban-seimitsu"
            // This route doesn't have an icon
            startIcon={<AssignmentIcon />}
            color="tertiary"
            text={taskManagementSeimitsuRoute.name}
            route={taskManagementSeimitsuRoute.route}
          />
        )}

        {taskManagementKenshinRoute && (
          <SideNavActionButton
            // This route doesn't have an icon
            dataTestId="sidenav-button-kanban-kenshin"
            startIcon={<AssignmentIcon />}
            color="tertiary"
            text={taskManagementKenshinRoute.name}
            route={taskManagementKenshinRoute.route}
          />
        )}

        <Divider
          sx={{
            margin: '1rem 0px',
            borderWidth: '1px',
            backgroundImage:
              'linear-gradient(to right, rgba(255, 255, 255, 0), rgb(255, 255, 255), rgba(255, 255, 255, 0)) !important',
          }}
        />

        {(histories || historiesKenshin) && (
          <SidenavList
            title={intl.get('dashboard_sidebar_list_title_received_and_sent')}>
            {historySentSeimitsuRoute && (
              <NavLink to={historySentSeimitsuRoute.route}>
                <SidenavListItem
                  color={color}
                  name={historySentSeimitsuRoute.name}
                  icon={histories.icon}
                  isClickable={true}
                  active={location.pathname === historySentSeimitsuRoute.route}
                />
              </NavLink>
            )}

            {historyReceivedSeimitsuRoute && (
              <NavLink to={historyReceivedSeimitsuRoute.route}>
                <SidenavListItem
                  color={color}
                  name={historyReceivedSeimitsuRoute.name}
                  icon={histories.icon}
                  isClickable={true}
                  active={
                    location.pathname === historyReceivedSeimitsuRoute.route
                  }
                />
              </NavLink>
            )}

            {historySentKenshinRoute && (
              <NavLink to={historySentKenshinRoute.route}>
                <SidenavListItem
                  color={color}
                  name={historySentKenshinRoute.name}
                  icon={historiesKenshin.icon}
                  isClickable={true}
                  active={location.pathname === historySentKenshinRoute.route}
                />
              </NavLink>
            )}

            {historyReceivedKenshinRoute && (
              <NavLink to={historyReceivedKenshinRoute.route}>
                <SidenavListItem
                  color={color}
                  name={historyReceivedKenshinRoute.name}
                  icon={historiesKenshin.icon}
                  isClickable={true}
                  active={
                    location.pathname === historyReceivedKenshinRoute.route
                  }
                />
              </NavLink>
            )}
          </SidenavList>
        )}

        {billing && (
          <SidenavList title={billing.name}>
            <NavLink to={billingSentRoute.route}>
              <SidenavListItem
                color={color}
                name={billingSentRoute.name}
                icon={billing.icon}
                isClickable={true}
                active={location.pathname === billingSentRoute.route}
              />
            </NavLink>
            <NavLink to={billingReceivedRoute.route}>
              <SidenavListItem
                color={color}
                name={billingReceivedRoute.name}
                icon={billing.icon}
                isClickable={true}
                active={location.pathname === billingReceivedRoute.route}
              />
            </NavLink>
          </SidenavList>
        )}

        {(paidWage || myWage) && (
          <SidenavList title={paidWage?.name || myWage?.name}>
            {paidWageDetails && (
              <NavLink to={paidWageDetails.route}>
                <SidenavListItem
                  color={color}
                  name={paidWage.name}
                  icon={paidWage.icon}
                  isClickable={true}
                  active={location.pathname === paidWageDetails.route}
                />
              </NavLink>
            )}
            {myWageDetails && (
              <NavLink to={myWageDetails.route}>
                <SidenavListItem
                  color={color}
                  name={myWage.name}
                  icon={myWage.icon}
                  isClickable={true}
                  active={location.pathname === myWageDetails.route}
                />
              </NavLink>
            )}
          </SidenavList>
        )}

        {assignment && (
          <SidenavList title={assignment.name}>
            {assignment_received && (
              <NavLink to={assignment_received.route}>
                <SidenavListItem
                  color={color}
                  name={assignment_received.name}
                  icon={assignment_received.icon}
                  isClickable={true}
                  active={location.pathname === assignment_received.route}
                />
              </NavLink>
            )}

            {assignment_sent && (
              <NavLink to={assignment_sent.route}>
                <SidenavListItem
                  color={color}
                  name={assignment_sent.name}
                  icon={assignment_sent.icon}
                  isClickable={true}
                  active={location.pathname === assignment_sent.route}
                />
              </NavLink>
            )}

            {assignment_received_kenshin && (
              <NavLink to={assignment_received_kenshin.route}>
                <SidenavListItem
                  name={assignment_received_kenshin.name}
                  icon={assignment_received_kenshin.icon}
                  isClickable={true}
                  active={
                    location.pathname === assignment_received_kenshin.route
                  }
                />
              </NavLink>
            )}
          </SidenavList>
        )}

        {contacts && (
          <SidenavList title={contacts.name}>
            {contacts_friends && (
              <NavLink to={contacts_friends.route}>
                <SidenavListItem
                  color={color}
                  name={contacts_friends.name}
                  icon={contacts_friends.icon}
                  isClickable={true}
                  active={location.pathname === contacts_friends.route}
                />
              </NavLink>
            )}
          </SidenavList>
        )}

        <SidenavListCollapse
          title={intl.get('dashboard_sidebar_list_title_others')}
          active={true}
          open={openMenuCollapse}
          handleClick={() => setOpenMenuCollapse(!openMenuCollapse)}>
          <NavLink to={'/dashboard/personal-settings'}>
            <SidenavListItem
              color={color}
              customMarginX="16px"
              customPaddingLeft="8px"
              name={intl.get('dashboard_text_page_title_personal_settings')}
              icon={<Icon fontSize="medium">settings</Icon>}
              isClickable={true}
              active={location.pathname === '/dashboard/personal-settings'}
            />
          </NavLink>

          {orgSettings && (
            <NavLink to={orgSettings.route}>
              <SidenavListItem
                color={color}
                customMarginX="16px"
                customPaddingLeft="8px"
                name={orgSettings.name}
                icon={orgSettings.icon}
                isClickable={true}
                active={location.pathname === orgSettings.route}
              />
            </NavLink>
          )}

          {logs && (
            <NavLink to={logs.route}>
              <SidenavListItem
                color={color}
                customMarginX="16px"
                customPaddingLeft="8px"
                name={logs.name}
                icon={logs.icon}
                isClickable={true}
                active={location.pathname === logs.route}
              />
            </NavLink>
          )}

          {manual && (
            <NavLink to={manual.route}>
              <SidenavListItem
                color={color}
                customMarginX="16px"
                customPaddingLeft="8px"
                name={manual.name}
                icon={manual.icon}
                isClickable={true}
                active={location.pathname === manual.route}
              />
            </NavLink>
          )}

          {serviceTerms && (
            <NavLink to={serviceTerms.route}>
              <SidenavListItem
                color={color}
                customMarginX="16px"
                customPaddingLeft="8px"
                name={serviceTerms.name}
                icon={serviceTerms.icon}
                isClickable={true}
                active={location.pathname === serviceTerms.route}
              />
            </NavLink>
          )}
        </SidenavListCollapse>
        <Box
          sx={{
            padding: '16px',
            textAlign: 'center',
          }}>
          <Typography
            variant="body2"
            color="white"
            sx={{
              fontSize: '12px',
            }}>
            {intl.get('dashboard_text_version_current', {
              version: import.meta.env.VITE_APP_VERSION,
            })}
          </Typography>
        </Box>
      </SidenavRoot>
    </Box>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'dark',
  ]),
  openDrawer: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.object),
  userInfo: PropTypes.object,
  handleClickLogout: PropTypes.func,
};

export default Sidenav;
